import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreate, useMeta, useResource } from '../../engine/api/hooks';
import { useApi } from '../../engine/api/context';
import { getPath } from '../../engine/meta/path';
import { AutoForm } from '../../engine/forms/AutoForm';
import { Field, Select } from '../../engine/design/primitives/Input';
import { Skeleton } from '../../engine/design/primitives/Misc';
import type { FieldMeta } from '../../engine/meta/types';
import { conditionsFor, useTicketTypes, type FieldConfigEntry, type TypeRow, fieldConfigOf, orderTicketFields } from './lib/catalog';

// The new-ticket form (ScreenDef.form.component for the tickets list): pick a ticket type,
// then the fields the type's `field_config` exposes to this audience (agent_new here; the
// portal renders user_new / anon from the same config). `required` / `readonly` / `hidden`
// come from the config; everything else is the resource's meta-driven AutoForm.

export type Audience = 'agent_new' | 'agent_detail' | 'user_new' | 'user_detail' | 'anon';

/** Apply a type's field_config to the resource meta: hide, require, mark read-only, relabel. */
export function applyFieldConfig(fields: FieldMeta[], config: Record<string, FieldConfigEntry> | undefined, opts: { mode: 'create' | 'edit' }): FieldMeta[] {
  if (!config || Object.keys(config).length === 0) return fields;
  const out: FieldMeta[] = [];
  for (const f of fields) {
    const c = config[f.name];
    // The rules are the whole form, on create AND on the inspector: an unlisted field is hidden
    // (docs/testing/issues.md S1-4). The type's profile supplies the agent_detail rules by default.
    if (!c) continue;
    if (c.visibility === 'hidden') continue;
    // A derived value (priority in matrix mode) has nothing to show before the record exists.
    if (c.visibility === 'readonly' && opts.mode === 'create') continue;
    out.push({ ...f, label: c.label ?? f.label, group: c.group ?? f.group, help: c.hint ?? f.help, required: c.visibility === 'required', readOnly: c.visibility === 'readonly' || f.readOnly });
  }
  return out;
}

/**
 * Runtime field behaviour of a type whose form comes from a connected system (a Halo ticket type):
 * dynamic dropdowns load their options from the type's lookup with the form's live values, and a
 * mapped-outcome field fills other fields when a value is chosen. A NovaPSA type has neither.
 */
function useTypeLookups(typeId: string | null, config: Record<string, FieldConfigEntry> | undefined) {
  const api = useApi();
  const [options, setOptions] = useState<Record<string, { value: string; label: string }[]>>({});
  const [patch, setPatch] = useState<Record<string, unknown> | null>(null);
  const [ctx, setCtx] = useState<{ client_id: string; site_id: string; contact_id: string; custom: string }>({ client_id: '', site_id: '', contact_id: '', custom: '{}' });
  const lastLookup = useRef<Record<string, string>>({});
  const dynamic = useMemo(() => Object.entries(config ?? {}).filter(([, e]) => e.dynamic && e.halo_field_id), [config]);
  const lookups = useMemo(() => Object.entries(config ?? {}).filter(([, e]) => e.database_lookup_id), [config]);

  useEffect(() => {
    setOptions({});
    setPatch(null);
    lastLookup.current = {};
  }, [typeId]);

  // Dynamic dropdowns: (re)load when the type, the customer or the other custom values change.
  useEffect(() => {
    if (!typeId || dynamic.length === 0) return;
    let cancelled = false;
    void Promise.all(dynamic.map(async ([key, e]) => {
      const q = new URLSearchParams({ halo_field_id: String(e.halo_field_id), custom_fields: ctx.custom });
      for (const k of ['client_id', 'site_id', 'contact_id'] as const) if (ctx[k]) q.set(k, ctx[k]);
      const res = await api.request<{ data: { value: string; label: string }[] }>(`/v1/ticket-types/${typeId}/field-options?${q.toString()}`).catch(() => ({ data: [] }));
      return [key, res.data] as const;
    })).then((rows) => {
      if (!cancelled) setOptions(Object.fromEntries(rows));
    });
    return () => {
      cancelled = true;
    };
  }, [api, typeId, dynamic, ctx]);

  const onValuesChange = useCallback((values: Record<string, unknown>) => {
    const custom = (values.custom_fields ?? {}) as Record<string, unknown>;
    const next = {
      client_id: String(values.client_id ?? ''),
      site_id: String(values.site_id ?? ''),
      contact_id: String(values.contact_id ?? ''),
      custom: JSON.stringify(Object.fromEntries(Object.entries(custom).filter(([, v]) => v !== null && v !== '' && v !== undefined).map(([k, v]) => [`custom_fields.${k}`, v]))),
    };
    setCtx((prev) => (prev.client_id === next.client_id && prev.site_id === next.site_id && prev.contact_id === next.contact_id && prev.custom === next.custom ? prev : next));
    // Mapped outcomes: a newly chosen value runs the lookup once and fills the fields it names.
    if (!typeId) return;
    for (const [key, e] of lookups) {
      const v = getPath(values, key);
      const value = v === null || v === undefined ? '' : String(v);
      if (!value || lastLookup.current[key] === value) continue;
      lastLookup.current[key] = value;
      void api
        .request<{ values: Record<string, unknown> }>(`/v1/ticket-types/${typeId}/field-lookup`, {
          method: 'POST',
          body: { database_lookup_id: e.database_lookup_id, value, client_id: next.client_id || null, site_id: next.site_id || null, contact_id: next.contact_id || null, custom_fields: JSON.parse(next.custom) as Record<string, unknown> },
        })
        .then((r) => {
          if (Object.keys(r.values).length) setPatch({ ...r.values });
        })
        .catch(() => undefined);
    }
  }, [api, typeId, lookups]);

  return { options, patch, onValuesChange };
}

export function NewTicketForm({ onDone, onCancel, audience = 'agent_new' }: { onDone: (row?: Record<string, unknown>) => void; onCancel: () => void; id?: string; audience?: Audience }) {
  const meta = useMeta('tickets');
  const types = useTicketTypes();
  const navigate = useNavigate();
  const create = useCreate('tickets', { toast: { success: (r) => `Ticket ${(r as { ref?: string }).ref ?? ''} created` } });
  const [typeId, setTypeId] = useState<string | null>(null);
  const listed = useMemo(() => types.rows.find((t) => t.id === typeId) ?? types.rows.find((t) => t.is_default) ?? types.rows[0] ?? null, [types.rows, typeId]);
  // The chosen type's full record: a connected system's type carries its real form only there.
  const detail = useResource<TypeRow>('ticket-types', listed?.id ?? null, { enabled: Boolean(listed) });
  const type = detail.data && detail.data.id === listed?.id ? detail.data : listed;
  const config = fieldConfigOf(type, audience);
  const lookups = useTypeLookups(type?.id ?? null, config);
  const conditions = useMemo(() => conditionsFor(config, (v, k) => getPath(v, k)), [config]);

  const fields = useMemo(() => {
    if (!meta.data) return [];
    const base = meta.data.fields.filter((f) => f.inCreate && !f.readOnly && f.name !== 'type_id' && f.name !== 'ref' && f.name !== 'source');
    const applied = applyFieldConfig(base, config, { mode: 'create' }).map((f) => {
      const opts = lookups.options[f.name];
      return opts ? { ...f, type: 'enum' as const, enum: opts } : f;
    });
    return orderTicketFields(applied);
  }, [meta.data, config, lookups.options]);

  if (meta.isLoading || types.isLoading) return <Skeleton lines={6} />;
  return (
    <div className="hf-stack">
      <Field label="Ticket type" id="sd-new-type">
        <Select
          id="sd-new-type"
          value={type?.id ?? ''}
          onChange={(e) => setTypeId(e.target.value || null)}
          options={types.rows.filter((t) => t.itil_kind !== 'task' || audience.startsWith('agent')).map((t) => ({ value: t.id, label: t.name }))}
        />
      </Field>
      {type?.effective?.priority_mode === 'matrix' && (
        <p className="hf-muted" style={{ fontSize: 'var(--t-12)', margin: 0 }}>Priority is derived from impact × urgency for this type.</p>
      )}
      <AutoForm
        key={`${type?.id ?? 'none'}:${detail.data ? 'full' : 'list'}`}
        fields={fields}
        mode="create"
        collapseSecondary
        conditions={conditions}
        onValuesChange={lookups.onValuesChange}
        patch={lookups.patch}
        submitLabel="Create ticket"
        onCancel={onCancel}
        onSubmit={async (values) => {
          const row = await create.mutateAsync({ ...values, ...(type ? { type_id: type.id } : {}) } as Record<string, unknown>);
          onDone(row as Record<string, unknown>);
          const id = (row as { id?: string }).id;
          if (id) navigate(`/tickets/${id}`);
        }}
      />
    </div>
  );
}
